import { CalendarClock, CalendarPlus } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/cargofl-ui';
import { CalendarPanel } from '@/components/timesheet/calendar-panel';
import {
  fetchConnectUrl, useCalendarConnection, useCalendarSuggestions,
} from '@/lib/calendar-api';

/**
 * One line on My week, above the grid. Nothing else.
 *
 * The shipped version rendered every suggestion as a row above the grid - 26
 * rows for one real week, the same standup three times, on every page load.
 * "No user will even know that this functionality exists" was the opposite
 * failure mode hiding behind that noise: a person who dismissed the whole
 * block (mentally, if not literally) would never notice a NEW meeting had
 * appeared. This component is the fix for both - a single line that is either
 * clearly informative or not there at all, and a Connect prompt when there is
 * nothing to review yet because nothing is connected.
 */
export function CalendarBanner({ week, timeZone }: { week: string; timeZone: string }) {
  const connection = useCalendarConnection();
  const suggestions = useCalendarSuggestions(week);
  const [open, setOpen] = React.useState(false);
  const [connecting, setConnecting] = React.useState(false);
  const [connectError, setConnectError] = React.useState<string | null>(null);

  // Reserve a fixed-height line rather than nothing-then-something: both
  // queries resolve independently, and letting either one pop the banner in
  // AFTER the grid has already painted is exactly the reflow the brief asks
  // to avoid. A row that is sometimes blank still holds its place.
  if (connection.isPending || suggestions.isPending) {
    return <div className="h-9" aria-hidden />;
  }

  // Neither query returned - fail closed to nothing rather than guessing at a
  // state we have no data for.
  if (!connection.data || !suggestions.data) return <div className="h-9" aria-hidden />;

  const connect = async () => {
    setConnectError(null);
    setConnecting(true);
    try {
      const { url } = await fetchConnectUrl();
      window.location.href = url;
    } catch {
      setConnectError('Could not reach the calendar connection service.');
      setConnecting(false);
    }
  };

  // Not connected: this IS the entire answer to "no user will even know this
  // exists". `suggestions.data.connected` and `connection.data.connectedAt`
  // describe the same fact from two endpoints that already existed before this
  // task; either is authoritative, so either not-connected is enough to show it.
  if (!suggestions.data.connected) {
    return (
      <div className="flex h-9 items-center justify-between gap-3 rounded-md border border-dashed border-muted-foreground/40 bg-muted/20 px-3 text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <CalendarPlus className="size-4" />
          Fill your week from Google Calendar
        </span>
        <Button hint={"Connect your Google Calendar (read-only) to fill in meetings"} size="sm" variant="outline" disabled={connecting} onClick={() => void connect()}>
          {connecting ? 'Connecting…' : 'Connect'}
        </Button>
        {connectError && <span className="text-xs text-destructive">{connectError}</span>}
      </div>
    );
  }

  if (!suggestions.data.reachable) {
    return (
      <div className="flex h-9 items-center gap-1.5 px-1 text-sm text-muted-foreground">
        <CalendarClock className="size-4" />
        Couldn&rsquo;t reach your calendar.
      </div>
    );
  }

  const untriaged = suggestions.data.groups.length;

  // Connected, reachable, nothing to triage: render nothing at all. Not even
  // the reserved-height div - the grid should look exactly as it does to
  // somebody with an empty calendar this week.
  if (untriaged === 0) return null;

  return (
    <>
      <div className="flex h-9 items-center justify-between gap-3 rounded-md border border-dashed border-muted-foreground/40 bg-muted/20 px-3 text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <CalendarClock className="size-4" />
          📅 {untriaged} meeting{untriaged === 1 ? '' : 's'} from your calendar
        </span>
        <Button hint={"See your meetings from Google Calendar and add them to this week"} size="sm" variant="outline" onClick={() => setOpen(true)}>
          Review
        </Button>
      </div>
      <CalendarPanel open={open} onOpenChange={setOpen} week={week} timeZone={timeZone} />
    </>
  );
}
